'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { TemplateCardSkeleton } from '@/components/ui/SkeletonLoader';
import { fetchPopularTemplates, type PopularTemplate } from '@/lib/popularity';

const platformColors: Record<string, string> = {
  shopify: '#96BF48',
  wordpress: '#21759B',
  woocommerce: '#7F54B3',
  html: '#E8633A',
  nextjs: '#000000',
  webflow: '#4353FF',
};

function StarRating({ rating, reviews }: { rating: number; reviews: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Icon
            key={star}
            name="StarIcon"
            size={12}
            variant={star <= Math.floor(rating) ? 'solid' : 'outline'}
            className={star <= Math.floor(rating) ? 'star-filled' : 'star-empty'}
          />
        ))}
      </div>
      <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
        {rating.toFixed(1)} ({reviews})
      </span>
    </div>
  );
}

export default function PopularTemplates() {
  const sectionRef = useRef<HTMLElement>(null);
  const [templates, setTemplates] = useState<PopularTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    fetchPopularTemplates(6)
      .then((data) => setTemplates(data))
      .catch((err) => console.error('[PopularTemplates]', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.08 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading]);

  // Don't render section if no templates
  if (!loading && templates.length === 0) return null;

  return (
    <section
      ref={sectionRef}
      className="py-24 lg:py-32"
      style={{ background: 'var(--bg-primary)' }}
    >
      <div className="max-w-8xl mx-auto px-6">
        <div className="reveal flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <span
              className="text-2xs font-bold uppercase tracking-[0.2em] mb-3 block"
              style={{ color: 'var(--accent)' }}
            >
              Trending Now
            </span>
            <h2
              className="font-display font-light text-4xl lg:text-6xl leading-tight"
              style={{ color: 'var(--text-primary)' }}
            >
              Popular{' '}
              <span className="italic">templates.</span>
            </h2>
          </div>
          <Link href="/templates" className="btn-secondary shrink-0">
            View All
            <Icon name="ArrowRightIcon" size={14} />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <TemplateCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((tpl, i) => {
              const platformColor = platformColors[tpl.platform.toLowerCase()] || '#6B7280';
              const imageUrl = tpl.thumbnail_url || '/assets/images/no_image.png';
              const altText = `${tpl.name} ${tpl.platform} template${tpl.category ? ` for ${tpl.category}` : ''}`;

              return (
                <Link
                  key={tpl.id}
                  href={`/template-detail?id=${tpl.id}`}
                  className="reveal template-card rounded-2xl overflow-hidden group"
                  style={{ animationDelay: `${i * 80}ms` }}
                  onMouseEnter={() => setHoveredId(tpl.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  {/* Rank badge */}
                  <div className="card-img-wrap relative h-48">
                    <AppImage src={imageUrl} alt={altText} fill className="object-cover" />
                    <div
                      className="absolute inset-0 flex items-center justify-center gap-3 transition-opacity duration-300"
                      style={{
                        opacity: hoveredId === tpl.id ? 1 : 0,
                        background: 'rgba(13,17,23,0.6)',
                      }}
                    >
                      <span className="btn-primary text-xs py-2 px-4">Quick View</span>
                    </div>
                    <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                      <span
                        className="badge"
                        style={{ background: 'var(--accent)', color: 'white' }}
                      >
                        #{i + 1} Popular
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <span
                          className="badge mb-1.5"
                          style={{
                            background: `${platformColor}18`,
                            color: platformColor,
                          }}
                        >
                          {tpl.platform.charAt(0).toUpperCase() + tpl.platform.slice(1)}
                        </span>
                        <h3 className="font-semibold text-base" style={{ color: 'var(--text-primary)' }}>
                          {tpl.name}
                        </h3>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="price-tag text-lg">${tpl.price.toFixed(0)}</p>
                      </div>
                    </div>

                    <StarRating rating={tpl.rating || 0} reviews={tpl.reviews_count || 0} />

                    {tpl.tags && tpl.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {tpl.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="platform-tag text-2xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div
                      className="flex items-center gap-1.5 mt-3 pt-3"
                      style={{ borderTop: '1px solid var(--border)' }}
                    >
                      <Icon
                        name="ArrowDownTrayIcon"
                        size={12}
                        style={{ color: 'var(--text-muted)' } as React.CSSProperties}
                      />
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {tpl.downloads_count.toLocaleString()} downloads
                      </span>
                      <span className="ml-auto text-xs" style={{ color: 'var(--text-muted)' }}>
                        Score: {tpl.popularity_score.toFixed(0)}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
